import {useEffect, useEffectEvent, useRef, useState} from 'react'
import {AnchoredOverlay, Button} from '@primer/react'
import {DotFillIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {Environment} from '@github-ui/agent-sessions-types/environment'
import {
  ENVIRONMENT_KIND_ICONS,
  ENVIRONMENT_KIND_LABELS,
  ENVIRONMENT_STATUS_DOT_COLOR_CLASS,
  ENVIRONMENT_STATUS_LABELS,
  getEnvironmentDisplayName,
} from '@github-ui/copilot-shared-ui/environments/environment-display'
import styles from './EnvironmentInfo.module.css'

export interface EnvironmentInfoProps {
  /** The AHP environment the task is running within. */
  environment: Environment
  /**
   * The environment's working directory, shown when known. Only applicable to
   * `user-local` environments where the daemon runs against a directory on the
   * user's machine; omit it for managed environments.
   */
  workingDirectory?: string
  /** Notified when the overlay is opened or closed, e.g. for analytics. */
  onToggleDetails?: (open: boolean) => void
  /** Freshness state for the retained environment record. */
  statusFreshness?: string
  className?: string
}

interface DetailRow {
  label: string
  value: string
  /** Renders the value in a monospace, wrapping cell (e.g. file paths). */
  monospace?: boolean
}

function buildDetailRows(
  environment: Environment,
  workingDirectory: string | undefined,
  statusFreshness: string | undefined,
): DetailRow[] {
  const rows: DetailRow[] = [{label: 'Type', value: ENVIRONMENT_KIND_LABELS[environment.kind]}]
  if (statusFreshness) rows.push({label: 'Status freshness', value: statusFreshness})

  const ahpVersion = environment.capabilities?.ahp_version
  if (ahpVersion) rows.push({label: 'AHP version', value: ahpVersion})

  if (workingDirectory) rows.push({label: 'Working directory', value: workingDirectory, monospace: true})

  const features = environment.capabilities?.features
  if (features && features.length > 0) rows.push({label: 'Features', value: features.join(', ')})

  const {current_sessions: current, max_sessions: max} = environment.capabilities ?? {}
  if (typeof max === 'number') {
    rows.push({label: 'Sessions', value: `${typeof current === 'number' ? current : 0} / ${max}`})
  }

  return rows
}

/**
 * Compact trigger that summarizes the AHP environment a task is running within.
 * The header shows the environment kind icon; activating it opens an overlay
 * that discloses the environment display name, status, compute type, AHP
 * version, working directory, advertised features, and session capacity so the
 * details stay out of the header until requested.
 */
export function EnvironmentInfo({
  environment,
  workingDirectory,
  onToggleDetails,
  statusFreshness,
  className,
}: EnvironmentInfoProps) {
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const notifyUnmountedWhileOpen = useEffectEvent(() => onToggleDetails?.(false))
  const statusLabel = ENVIRONMENT_STATUS_LABELS[environment.status]
  const environmentName =
    environment.kind === 'managed-sandbox' ? getEnvironmentDisplayName(environment) : environment.name
  const EnvironmentIcon = ENVIRONMENT_KIND_ICONS[environment.kind]
  const detailRows = buildDetailRows(environment, workingDirectory, statusFreshness)

  useEffect(
    () => () => {
      if (openRef.current) notifyUnmountedWhileOpen()
    },
    [],
  )

  const handleOpen = () => {
    openRef.current = true
    setOpen(true)
    onToggleDetails?.(true)
  }

  const handleClose = () => {
    openRef.current = false
    setOpen(false)
    onToggleDetails?.(false)
  }

  return (
    <span className={clsx(styles.environmentInfo, className)} data-testid="environment-info">
      <AnchoredOverlay
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        align="start"
        side="outside-bottom"
        overlayProps={{role: 'dialog', 'aria-label': `${environmentName} environment details`}}
        renderAnchor={anchorProps => (
          <Button
            {...anchorProps}
            type="button"
            leadingVisual={EnvironmentIcon}
            variant="invisible"
            size="small"
            className={styles.trigger}
            aria-label={`Environment: ${environmentName} (${statusLabel})`}
            data-testid="environment-info-toggle"
          >
            {environmentName}
          </Button>
        )}
      >
        <div className={styles.overlay} data-testid="environment-details">
          <div className={styles.overlayHeader}>
            <EnvironmentIcon size={16} className={styles.overlayHeaderIcon} aria-hidden="true" />
            <span className={styles.overlayTitle} data-testid="environment-name">
              {environmentName}
            </span>
            <span className={styles.status} data-testid="environment-status">
              <DotFillIcon
                size={14}
                className={ENVIRONMENT_STATUS_DOT_COLOR_CLASS[environment.status]}
                aria-hidden="true"
              />
              <span>{statusLabel}</span>
            </span>
          </div>
          <dl className={styles.details}>
            {detailRows.map(row => (
              <div key={row.label} className={styles.detailRow} data-testid={`environment-detail-${row.label}`}>
                <dt className={styles.detailLabel}>{row.label}</dt>
                <dd className={clsx(styles.detailValue, row.monospace && styles.monospace)}>{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </AnchoredOverlay>
    </span>
  )
}
